import type { AddFactiii, FactiiiTag } from '@shared/all/domains/factiiis';
import type { Post } from '@shared/all/domains/posts';
import type { PostSnapshot } from '@shared/all/helpers/postSnapshot';
import { databaseActions } from '../context/context';
import { useDatabaseContext } from '../context/DatabaseContext';
import { useUserState } from '../context/UserContext';
import { getBlobStorage } from '../database/blobStorage';
import { computeHash } from '../database/computeHash';
import { HashCollector, type HashRecord } from '../database/localDataHash';
import { DatabaseRepository, type PrivatePost } from '../database/repository';
import type { SourceType } from '../database/schema';
import type { FactiiiResolver } from '../database/types';
import { loadSeedData } from '../database/utils/loadSeedData';
// Seed utilities are dynamically imported at their (admin/debug-only) call
// sites: their static import pulled drizzle-seed — a ~1.5MB dev/test data
// generator — into the every-page client bundle via this app-wide hook.

// Detect if running on mobile (React Native) or web client
const getSource = (): SourceType => {
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    return 'mobile';
  }
  return 'client';
};
const SOURCE = getSource();
const convertPrivatePostToPost = async (privatePost: PrivatePost, user: Post['user'], factiiiResolver?: FactiiiResolver, edited?: boolean): Promise<Post> => {
  const blobStorage = await getBlobStorage(user.id);
  return {
    id: privatePost.id,
    slug: privatePost.slug ?? undefined,
    createdAt: privatePost.createdAt,
    parentPostId: privatePost.parentPostId ?? undefined,
    content: privatePost.content,
    uploads: privatePost.uploads.length > 0 ? await Promise.all(privatePost.uploads.map(async upload => ({
      id: upload.id,
      type: upload.type,
      name: null,
      isFactiii: false,
      display: {
        key: upload.key,
        width: null,
        height: null,
        contentType: 'application/octet-stream',
        url: (await blobStorage.getUrl(upload.key)) || ''
      }
    }))) : null,
    user: {
      id: user.id,
      username: user.username,
      name: user.name,
      robohash: user.robohash,
      tag: user.tag,
      avatarKey: user.avatarKey,
      anonymous: privatePost.anonymous
    },
    replies_count: privatePost.replies_count,
    voteCount: 0,
    viewCount: 0,
    voted: null,
    saved: false,
    // private posts only ever live in the author's own local database
    isAuthor: true,
    edited: edited ?? false,
    awards: [],
    postFactiiisCount: await Promise.all(privatePost.factiiisCount.map(async factiiiCount => {
      const factiii = await factiiiResolver?.getFactiiiId(factiiiCount.factiiiId);
      return {
        id: factiiiCount.id,
        factiiiId: factiiiCount.factiiiId,
        name: factiii?.name ?? `Factiii ${factiiiCount.factiiiId}`,
        requirements: factiii?.requirements ?? [],
        count: factiiiCount.count,
        avatarKey: factiii?.avatarKey ?? null,
        postFactiiis: await Promise.all(factiiiCount.postFactiiis.map(async pf => {
          // Resolve blob URLs for factiii uploads
          const uploadsWithUrls = await Promise.all(pf.uploads.map(async upload => ({
            id: upload.id,
            type: upload.type,
            name: null,
            isFactiii: true,
            display: {
              key: upload.key,
              width: null,
              height: null,
              contentType: 'application/octet-stream',
              url: (await blobStorage.getUrl(upload.key)) || ''
            }
          })));
          return {
            id: pf.id,
            data: pf.data || null,
            number: pf.number ?? null,
            createdAt: pf.createdAt,
            status: 'APPROVED',
            user: {
              id: user.id,
              username: user.username,
              name: user.name,
              robohash: user.robohash,
              tag: user.tag,
              avatarKey: user.avatarKey,
              anonymous: false
            },
            spaceTimes: pf.spaceTimes.map(st => ({
              id: st.id || 0,
              createdAt: st.createdAt,
              updatedAt: st.updatedAt,
              timestamp: st.timestamp instanceof Date ? st.timestamp : st.timestamp ? new Date(st.timestamp) : null,
              latitude: st.latitude || null,
              longitude: st.longitude || null,
              display: st.display || null,
              altitude: st.altitude || null,
              postFactiiiId: pf.id,
              uploads: uploadsWithUrls
            })),
            scrapeId: null,
            rawData: false,
            uploads: uploadsWithUrls,
            references: pf.references || [],
            factiiiReferences: await Promise.all((pf.factiiiReferences || []).map(async ref => {
              const refFactiii = await factiiiResolver?.getFactiiiId(ref.factiiiId);
              return {
                data: ref.data,
                spaceTimes: (ref.spaceTimes || []).map(st => ({
                  id: st.id,
                  createdAt: st.createdAt,
                  updatedAt: st.updatedAt,
                  timestamp: st.timestamp,
                  latitude: st.latitude,
                  longitude: st.longitude,
                  display: st.display,
                  altitude: st.altitude,
                  postFactiiiId: 0
                })),
                uploads: (ref.uploads || []).map(u => ({
                  id: u.id,
                  key: u.key
                })),
                factiii: {
                  id: ref.factiiiId,
                  avatarKey: refFactiii?.avatarKey ?? null,
                  name: refFactiii?.name ?? `Factiii ${ref.factiiiId}`,
                  requirements: (refFactiii?.requirements ?? []) as string[],
                  slug: refFactiii?.slug ?? `factiii-${ref.factiiiId}`
                }
              };
            })),
            triggeredByPostFactiii: null
          };
        }))
      };
    })),
    space: privatePost.spaceId ? `space-${privatePost.spaceId}` : undefined,
    // The whole body is in `content` — nothing to page in from a server.
    contentRows: 1,
    pinned: false,
    replies: privatePost.replies ? await Promise.all(privatePost.replies.map(reply => convertPrivatePostToPost(reply, user, factiiiResolver))) : [],
    isPrivate: true,
    // Device-local: this row lives in SQLite on this device and was never sent
    // to a server, which is the opposite of `isServerPrivate`. Routing and the
    // badge copy both branch on the difference.
    isServerPrivate: false,
    // Nor a server draft: this row has no server-side status at all, so the
    // question the board emitters ask of it — may a wider audience see this? —
    // is answered by isPrivate above.
    isServerDraft: false,
    triggeredByFactiii: null
  };
};
const useDatabase = (factiiiResolver?: FactiiiResolver) => {
  const {
    state: {
      db,
      disabled,
      canWrite,
      hashingDisabled
    },
    dispatch,
    acknowledgePopup,
    triggerError,
    reportErrorToServer,
    syncHashes,
    setHashingDisabled,
    userId
  } = useDatabaseContext();
  const {
    state: userState
  } = useUserState();
  const currentUser = userState.user;
  const postUser: Post['user'] | null = currentUser?.isAuthenticated ? {
    id: currentUser.id,
    username: currentUser.username,
    name: currentUser.name,
    robohash: currentUser.robohash || '',
    tag: currentUser.tag,
    avatarKey: currentUser.avatarKey,
    anonymous: false
  } : null;

  // Create repository instance (null if no DB)
  const repo = db ? new DatabaseRepository(db, SOURCE) : null;

  // Helper to mark records with timestamp when hash was sent
  const markHashesSent = async (upserts: HashRecord[], storedHashes: string[]) => {
    if (!repo || storedHashes.length === 0) return;

    // Filter upserts to only those that were stored (exclude blobs)
    const storedSet = new Set(storedHashes);
    const storedUpserts = upserts.filter(h => storedSet.has(h.id) && h.tableName !== 'blob');
    if (storedUpserts.length === 0) return;
    const now = Date.now();

    // Update hash_sent timestamp for snapshot records only
    for (const {
      id,
      tableName
    } of storedUpserts) {
      try {
        if (tableName === 'private_post_snapshot') {
          await repo.markHashSent(id, now);
        }
      } catch {
        // Record may have been deleted
      }
    }
  };

  // Helper to sync hashes without blocking
  const syncHashesAsync = (upserts_0: HashRecord[] = [], deletes: string[] = []) => {
    // Skip if hashing is disabled
    if (hashingDisabled) return;
    if (!syncHashes || upserts_0.length === 0 && deletes.length === 0) return;
    syncHashes({
      upserts: upserts_0,
      deletes
    }).then(({
      storedHashes: storedHashes_0
    }) => {
      if (upserts_0.length > 0 && storedHashes_0.length > 0) {
        markHashesSent(upserts_0, storedHashes_0).catch(() => {});
      }
    }).catch(() => {});
  };

  // Sync all unsent snapshot hashes to server
  const syncUnsentHashes = async () => {
    if (!repo || !syncHashes) return;
    const collector = new HashCollector(repo);

    // Query snapshot records needing sync (hash_sent is null)
    const unsentSnapshots = await repo.getUnsentSnapshots();

    // Compute and add snapshot hashes to collector
    for (const snapshotRecord of unsentSnapshots) {
      const snapshot = await repo.collectStoredSnapshot(snapshotRecord.id);
      if (snapshot) {
        const hash = await computeHash(JSON.stringify(snapshot));
        collector.addPostSnapshot(snapshotRecord.id, hash);
      }
    }
    const hashes = collector.getHashes();
    if (hashes.length === 0) return;

    // Sync and mark as sent
    const {
      storedHashes: storedHashes_1
    } = await syncHashes({
      upserts: hashes
    });
    await markHashesSent(hashes, storedHashes_1);
  };

  // Toggle hashing and sync unsent if re-enabling
  const toggleHashing = async (disabledValue: boolean) => {
    await setHashingDisabled(disabledValue);
    if (!disabledValue) {
      // Re-enabled - sync all unsent hashes
      await syncUnsentHashes();
    }
  };
  const acknowledgeError = async () => {
    await acknowledgePopup();
  };

  // Helper to categorize errors and determine if DB should be paused
  const isCriticalError = (error: unknown): boolean => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      // Critical errors that should pause the database
      return message.includes('quota exceeded') || message.includes('database locked') || message.includes('corruption') || message.includes('schema') || message.includes('migration') || message.includes('not initialized') || message.includes('connection');
    }
    return false;
  };

  // Helper to handle errors with proper categorization
  const handleError = async (error_0: unknown, context: string, shouldPauseOnCritical = true): Promise<void> => {
    const errorMessage = error_0 instanceof Error ? error_0.message : 'Unknown error';
    const errorStack = error_0 instanceof Error ? error_0.stack || 'No stack trace available' : 'No stack trace available';
    console.error(`useDatabase: ${context}:`, error_0);

    // Report critical errors to server if reporter is available
    if (shouldPauseOnCritical && isCriticalError(error_0) && reportErrorToServer) {
      // Fire and forget - don't block on error reporting
      reportErrorToServer(`Database Error: Critical database error in ${context}: ${errorMessage}`, errorStack).catch(() => {
        // Silently fail - error reporting should never block
      });
      await triggerError(`Critical database error in ${context}: ${errorMessage}`);
    } else if (shouldPauseOnCritical && isCriticalError(error_0)) {
      await triggerError(`Critical database error in ${context}: ${errorMessage}`);
    } else {
      // For non-critical errors, log but don't pause DB
      await triggerError(`Failed ${context}: ${errorMessage}`);
    }
  };

  // Generic read helper: checks repo + disabled, returns fallback on error
  async function readQuery<T>(fallback: T, context_0: string, fn: (r: DatabaseRepository) => Promise<T>): Promise<T> {
    if (!repo || disabled) return fallback;
    try {
      return await fn(repo);
    } catch (error_1) {
      await handleError(error_1, context_0, false);
      return fallback;
    }
  }

  // Generic write helper: checks repo + canWrite, returns fallback on error
  async function writeOp<T>(fallback_0: T, context_1: string, fn_0: (r: DatabaseRepository) => Promise<T>): Promise<T> {
    if (!repo || !canWrite) return fallback_0;
    try {
      return await fn_0(repo);
    } catch (error_2) {
      await handleError(error_2, context_1, true);
      return fallback_0;
    }
  }
  const addOrUpdatePostEngagement = async (post: Post, duration: number, viewedAt: Date) => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) return;

    // STRICT VALIDATION: Reject null/undefined values before they corrupt the database
    if (!post?.id) {
      console.error('[DB] REJECTED: post.id is invalid:', post?.id);
      return;
    }
    if (duration == null || !Number.isFinite(duration)) {
      console.error('[DB] REJECTED: duration is invalid:', duration);
      return;
    }
    if (!(viewedAt instanceof Date) || isNaN(viewedAt.getTime())) {
      console.error('[DB] REJECTED: viewedAt is invalid:', viewedAt);
      return;
    }
    try {
      await repo.addOrUpdatePostEngagement(String(post.id), duration, viewedAt.getTime());

      // Then, save factiii engagements
      if (post.postFactiiisCount && post.postFactiiisCount.length > 0) {
        await Promise.all(post.postFactiiisCount.map(postFactiiiCount => addOrUpdateFactiiiEngagement(postFactiiiCount.factiiiId, postFactiiiCount.name, duration, viewedAt)));
      }
    } catch (error_3) {
      await handleError(error_3, 'saving post engagement', true);
    }
  };
  const addOrUpdateFactiiiEngagement = async (factiiiId: number, name: string, duration_0: number, viewedAt_0: Date) => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) return;

    // STRICT VALIDATION
    if (factiiiId == null || !Number.isFinite(factiiiId)) {
      console.error('[DB] REJECTED: factiiiId is invalid:', factiiiId);
      return;
    }
    if (name == null || typeof name !== 'string') {
      console.error('[DB] REJECTED: name is invalid:', name);
      return;
    }
    if (duration_0 == null || !Number.isFinite(duration_0)) {
      console.error('[DB] REJECTED: duration is invalid:', duration_0);
      return;
    }
    if (!(viewedAt_0 instanceof Date) || isNaN(viewedAt_0.getTime())) {
      console.error('[DB] REJECTED: viewedAt is invalid:', viewedAt_0);
      return;
    }
    try {
      await repo.addOrUpdateFactiiiEngagement(factiiiId, name, duration_0, viewedAt_0.getTime());
    } catch (error_4) {
      await handleError(error_4, 'saving factiii engagement', true);
    }
  };

  // Data retrieval functions
  const getPostEngagement = (postId: number) => readQuery(null, 'getting post engagement', r => r.getPostEngagement(String(postId)));
  const getAllPostEngagements = () => readQuery([], 'getting all post engagements', r_0 => r_0.getAllPostEngagements());
  const getPaginatedPostEngagements = async (page: number, limit = 20) => ({
    items: await readQuery([], 'getting paginated post engagements', r_1 => r_1.getPaginatedPostEngagements(page, limit))
  });
  const getAllFactiiiEngagements = () => readQuery([], 'getting all factiii engagements', r_2 => r_2.getAllFactiiiEngagements());
  const getFactiiiEngagementsByDateRange = (startDate: Date, endDate: Date) => readQuery([], 'getting factiii engagements by date range', r_3 => r_3.getFactiiiEngagementsByDateRange(startDate, endDate));
  const addMoodEntry = async (mood: 'angry' | 'sad' | 'happy' | 'laughing', date?: Date): Promise<void> => {
    await writeOp(undefined, 'saving mood entry', r_4 => r_4.addMoodEntry(mood, date));
  };
  const getMoodEntriesByDateRange = (startDate_0: Date, endDate_0: Date) => readQuery([], 'getting mood entries by date range', r_5 => r_5.getMoodEntriesByDateRange(startDate_0, endDate_0));
  const getLatestMoodEntry = () => readQuery(null, 'getting latest mood entry', r_6 => r_6.getLatestMoodEntry());
  const getHealthPostsByDateRange = (startDate_1: Date, endDate_1: Date) => readQuery([], 'getting health posts by date range', r_7 => r_7.getHealthPostsByDateRange(userId!, startDate_1, endDate_1));
  const createPrivatePost = async ({
    content,
    uploads,
    factiiis,
    anonymous,
    spaceId,
    parentPostId,
    skipHash = false
  }: {
    content: string;
    uploads: {
      uri: string;
    }[];
    factiiis: FactiiiTag[];
    anonymous: boolean;
    spaceId?: number;
    parentPostId?: string;
    /** Skip hash sync - use for mass imports to avoid spamming server */
    skipHash?: boolean;
  }): Promise<Post | null> => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite || !postUser) return null;
    try {
      const collector_0 = new HashCollector(repo);
      const blobStorage = await getBlobStorage(userId!);

      // Store blobs and build upload records
      const uploadRecords: Array<{
        key: string;
        type: string;
      }> = [];
      for (const uploadData of uploads) {
        const storeResult = await blobStorage.storeFile({
          uri: uploadData.uri
        });
        uploadRecords.push({
          key: storeResult.key,
          type: storeResult.mimeType
        });

        // Hash blob content
        const blobData = await blobStorage.getBlobData(storeResult.key);
        if (blobData) {
          await collector_0.addBlob({
            id: storeResult.key,
            name: storeResult.key,
            content: blobData,
            createdAt: Date.now()
          });
        }
      }

      // Build factiii params with photo upload records
      const factiiiParams = await Promise.all(factiiis.map(async factiii => ({
        factiiiId: factiii.factiiiId,
        postFactiiis: await Promise.all(factiii.postFactiiis.map(async pf => {
          let photoUploadRecord: {
            key: string;
            type: string;
          } | null = null;
          if (pf.photo) {
            const photoResult = await blobStorage.storeFile({
              uri: pf.photo.uri
            });
            photoUploadRecord = {
              key: photoResult.key,
              type: photoResult.mimeType
            };
            const blobData_0 = await blobStorage.getBlobData(photoResult.key);
            if (blobData_0) {
              await collector_0.addBlob({
                id: photoResult.key,
                name: photoResult.key,
                content: blobData_0,
                createdAt: Date.now()
              });
            }
          }
          return {
            anonymous: pf.anonymous,
            data: pf.data?.toString() ?? null,
            number: pf.number ?? null,
            spaceTimes: pf.spaceTimes?.map(({
              timestamp,
              latitude,
              longitude,
              display,
              altitude
            }) => ({
              timestamp: typeof timestamp === 'string' ? new Date(timestamp) : timestamp,
              latitude,
              longitude,
              display,
              altitude
            })),
            photoUploadRecord,
            references: pf.references ?? [],
            factiiiReferences: pf.factiiiReferences?.map(ref => ({
              factiiiId: ref.factiii.id,
              data: ref.data
            }))
          };
        }))
      })));
      const postId_0 = await repo.createPrivatePost({
        content,
        anonymous,
        userId: postUser.id,
        spaceId,
        parentPostId,
        uploadRecords,
        factiiis: factiiiParams
      });

      // Create snapshot and hash for initial post state
      if (!skipHash) {
        await collector_0.snapshotAndHash(postId_0);
        if (collector_0.hasHashes()) {
          syncHashesAsync(collector_0.getHashes());
        }
      }
      const privatePostData = await repo.buildPrivatePost((await repo.fetchPrivatePost(postId_0))!, postUser.id, false);
      return await convertPrivatePostToPost(privatePostData, postUser, factiiiResolver);
    } catch (error_5) {
      await handleError(error_5, 'creating private post', true);
      return null;
    }
  };
  const fetchPrivatePosts = async ({
    page: page_0
  }: {
    page: number;
  }): Promise<{
    posts: Post[];
  }> => {
    if (!repo || disabled || !postUser) return {
      posts: []
    };
    try {
      const postRows = await repo.fetchPrivatePosts(page_0);
      const postsWithData = await Promise.all(postRows.map(async postRow => {
        const privatePostData_0 = await repo.buildPrivatePost(postRow, postUser.id, false);
        const edited = await repo.wasContentEdited(postRow.id);
        return await convertPrivatePostToPost(privatePostData_0, postUser, factiiiResolver, edited);
      }));
      return {
        posts: postsWithData
      };
    } catch (error_6) {
      await handleError(error_6, 'fetching private posts', false);
      return {
        posts: []
      };
    }
  };
  const fetchPrivatePost = async (postId_1: string): Promise<Post | null> => {
    if (!repo || disabled || !postUser) return null;
    try {
      const postRow_0 = await repo.fetchPrivatePost(postId_1);
      if (!postRow_0) return null;
      const privatePostData_1 = await repo.buildPrivatePost(postRow_0, postUser.id, true);
      const edited_0 = await repo.wasContentEdited(postId_1);
      return await convertPrivatePostToPost(privatePostData_1, postUser, factiiiResolver, edited_0);
    } catch (error_7) {
      await handleError(error_7, 'fetching private post', false);
      return null;
    }
  };
  const fetchPrivatePostReplies = async ({
    postId: postId_2,
    page: page_1
  }: {
    postId: string;
    page: number;
  }): Promise<{
    replies: Post[];
  }> => {
    if (!repo || disabled || !postUser) return {
      replies: []
    };
    try {
      const replyRows = await repo.fetchPrivatePostReplies(postId_2, page_1);
      const repliesWithData = await Promise.all(replyRows.map(async replyRow => {
        const privateReplyData = await repo.buildPrivatePost(replyRow, postUser.id, true);
        const edited_1 = await repo.wasContentEdited(replyRow.id);
        return await convertPrivatePostToPost(privateReplyData, postUser, factiiiResolver, edited_1);
      }));
      return {
        replies: repliesWithData
      };
    } catch (error_8) {
      await handleError(error_8, 'fetching private post replies', false);
      return {
        replies: []
      };
    }
  };
  const deletePrivatePost = async (postId_3: string): Promise<boolean> => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite || !userId) return false;
    try {
      const {
        blobKeysToDelete,
        hashIdsToDelete
      } = await repo.deletePrivatePost(postId_3);

      // Delete hashes for all deleted records
      syncHashesAsync([], hashIdsToDelete);

      // Delete blobs after DB transaction succeeds
      const blobStorage_0 = await getBlobStorage(userId);
      const failedBlobDeletions: string[] = [];
      await Promise.all(blobKeysToDelete.map(async key => {
        try {
          await blobStorage_0.delete(key);
        } catch (error_10) {
          console.warn('Failed to delete blob after DB cleanup:', key, error_10);
          failedBlobDeletions.push(key);
        }
      }));
      if (failedBlobDeletions.length > 0) {
        console.warn(`[deletePrivatePost] ${failedBlobDeletions.length} blob(s) orphaned but DB records cleaned`);
      }
      return true;
    } catch (error_9) {
      await handleError(error_9, 'deleting private post', true);
      return false;
    }
  };
  const uploadFromBase64 = async ({
    base64,
    type = 'IMAGE',
    postId: postId_4
  }: {
    base64: string;
    type?: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    postId?: string;
  }): Promise<{
    id: string;
    key: string;
  } | null> => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) return null;
    try {
      const blobStorage_1 = await getBlobStorage(userId!);
      const collector_1 = new HashCollector(repo);
      const {
        key: key_0
      } = await blobStorage_1.storeFile({
        base64Data: base64
      });
      const uploadRecord = await repo.createUploadRecord({
        key: key_0,
        type,
        postId: postId_4
      });

      // AFTER upload: Hash blob + create snapshot for post
      await collector_1.addBlob({
        id: key_0,
        name: key_0,
        content: base64,
        createdAt: uploadRecord.createdAt
      });
      if (postId_4) {
        await collector_1.snapshotAndHash(postId_4);
      }
      syncHashesAsync(collector_1.getHashes());
      return {
        id: uploadRecord.id,
        key: key_0
      };
    } catch (error_11) {
      await handleError(error_11, 'uploading from base64', true);
      return null;
    }
  };
  const processPrivateFactiiis = async (postId_5: string, addFactiiis: AddFactiii[], removeFactiiiIds: number[], updateFactiiis: AddFactiii[] = []): Promise<boolean> => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) return false;
    try {
      const collector_2 = new HashCollector(repo);

      // Add factiiis
      if (addFactiiis.length > 0) {
        await repo.addFactiiis(postId_5, addFactiiis.map(f => ({
          factiiiId: f.factiiiId,
          anonymous: f.anonymous,
          data: f.data?.toString() ?? null,
          number: f.number ?? null,
          spaceTimes: f.spaceTimes?.map(({
            timestamp: timestamp_0,
            latitude: latitude_0,
            longitude: longitude_0,
            display: display_0,
            altitude: altitude_0
          }) => ({
            timestamp: typeof timestamp_0 === 'string' ? new Date(timestamp_0) : timestamp_0,
            latitude: latitude_0,
            longitude: longitude_0,
            display: display_0,
            altitude: altitude_0
          })),
          uploads: f.uploads,
          references: f.references,
          factiiiReferences: f.factiiiReferences?.map(ref_0 => ({
            factiiiId: ref_0.factiiiId,
            data: ref_0.data ?? null
          }))
        })));
      }

      // Update existing factiiis
      if (updateFactiiis.length > 0) {
        await repo.updateFactiiis(postId_5, updateFactiiis.map(f_0 => ({
          factiiiId: f_0.factiiiId,
          anonymous: f_0.anonymous,
          data: f_0.data !== undefined ? f_0.data?.toString() ?? null : undefined,
          number: f_0.number !== undefined ? f_0.number ?? null : undefined,
          spaceTimes: f_0.spaceTimes?.map(({
            timestamp: timestamp_1,
            latitude: latitude_1,
            longitude: longitude_1,
            display: display_1,
            altitude: altitude_1
          }) => ({
            timestamp: typeof timestamp_1 === 'string' ? new Date(timestamp_1) : timestamp_1,
            latitude: latitude_1,
            longitude: longitude_1,
            display: display_1,
            altitude: altitude_1
          })),
          uploads: f_0.uploads,
          references: f_0.references,
          factiiiReferences: f_0.factiiiReferences?.map(ref_1 => ({
            factiiiId: ref_1.factiiiId,
            data: ref_1.data ?? null
          }))
        })));
      }

      // Remove factiiis
      if (removeFactiiiIds.length > 0) {
        await repo.removeFactiiis(postId_5, removeFactiiiIds);
      }
      await collector_2.snapshotAndHash(postId_5);
      if (collector_2.hasHashes()) {
        syncHashesAsync(collector_2.getHashes());
      }
      return true;
    } catch (error_12) {
      await handleError(error_12, 'processing private factiiis', true);
      return false;
    }
  };
  const fetchPrivateMyFactiiiTags = async (postId_6: string, excludeFactiiiIds: number[] = []): Promise<FactiiiTag[]> => {
    if (!repo || disabled) return [];
    try {
      const factiiiCounts = await repo.getFactiiiCountsForPost(postId_6, userId!);
      const result = await Promise.all(factiiiCounts.filter(pfc => !excludeFactiiiIds.includes(pfc.factiiiId)).map(async pfc_0 => {
        const factiii_0 = await factiiiResolver?.getFactiiiId(pfc_0.factiiiId);
        const blobStorage_2 = await getBlobStorage(userId!);
        const factiiiTag: FactiiiTag = {
          id: pfc_0.id,
          isBot: false,
          factiiiId: pfc_0.factiiiId,
          avatarKey: factiii_0?.avatarKey ?? null,
          name: factiii_0?.name ?? `Factiii ${pfc_0.factiiiId}`,
          isRetired: factiii_0?.status === 'RETIRED',
          slug: factiii_0?.slug ?? `factiii-${pfc_0.factiiiId}`,
          spaceSlug: factiii_0?.spaceSlug ?? undefined,
          requirements: factiii_0?.requirements ?? [],
          description: factiii_0?.description ?? null,
          replyConfig: factiii_0?.replyConfig ?? undefined,
          isNewFactiii: false,
          isRemovedFactiii: false,
          isPinnedFactiii: false,
          postFactiiis: await Promise.all(pfc_0.postFactiiis.map(async pf_0 => {
            const uploadsWithUrls = await Promise.all(pf_0.uploads.map(async upload => ({
              id: upload.id,
              type: upload.type,
              name: null,
              isFactiii: true,
              display: {
                key: upload.key,
                width: null,
                height: null,
                contentType: 'application/octet-stream',
                url: (await blobStorage_2.getUrl(upload.key)) || ''
              }
            })));
            return {
              id: pf_0.id,
              data: pf_0.data || null,
              number: pf_0.number ?? null,
              spaceTimes: pf_0.spaceTimes,
              references: pf_0.references || [],
              factiiiReferences: await Promise.all((pf_0.factiiiReferences || []).map(async ref_2 => {
                const refFactiii = await factiiiResolver?.getFactiiiId(ref_2.factiiiId);
                return {
                  data: ref_2.data,
                  spaceTimes: (ref_2.spaceTimes || []).map(st => ({
                    id: st.id,
                    timestamp: st.timestamp,
                    latitude: st.latitude,
                    longitude: st.longitude,
                    display: st.display,
                    altitude: st.altitude
                  })),
                  uploads: (ref_2.uploads || []).map(u => ({
                    id: u.id,
                    display: {
                      key: u.key,
                      width: null,
                      height: null,
                      contentType: 'application/octet-stream'
                    }
                  })),
                  factiii: {
                    id: ref_2.factiiiId,
                    avatarKey: refFactiii?.avatarKey ?? null,
                    name: refFactiii?.name ?? `Factiii ${ref_2.factiiiId}`,
                    requirements: (refFactiii?.requirements ?? []) as string[],
                    slug: refFactiii?.slug ?? `factiii-${ref_2.factiiiId}`
                  }
                };
              })),
              anonymous: pf_0.anonymous || false,
              photo: undefined,
              uploads: uploadsWithUrls
            };
          }))
        };
        return factiiiTag;
      }));
      return result;
    } catch (error_13) {
      await handleError(error_13, 'fetching private my factiii tags', false);
      return [];
    }
  };

  // Advanced seed function with factiii engagements and mood tracking
  const seedAdvancedData = async (): Promise<void> => {
    if (!repo || !canWrite) return;
    try {
      const {
        seedAdvancedData: seedAdvancedDataUtil
      } = await loadSeedData();
      await seedAdvancedDataUtil(addMoodEntry, addOrUpdateFactiiiEngagement);
    } catch (error_14) {
      await handleError(error_14, 'seeding advanced data', true);
    }
  };
  const getPrivatePostWithBlobURLs = async (postRow_1: Awaited<ReturnType<DatabaseRepository['fetchPrivatePost']>>, includeReplies = false): Promise<PrivatePost> => {
    if (!repo || !postRow_1) throw new Error('Post not found');
    return repo.buildPrivatePost(postRow_1, postRow_1.userId!, includeReplies);
  };
  const editPrivatePost = async (postId_7: string, content_0: string, uploads_0?: {
    uri: string;
  }[]): Promise<boolean> => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) return false;
    let oldBlobKeysToDelete: string[] = [];
    try {
      const blobStorage_3 = await getBlobStorage(userId!);
      const collector_3 = new HashCollector(repo);

      // Update post content
      await repo.updatePostContent(postId_7, content_0);

      // Handle uploads if provided
      if (uploads_0 !== undefined) {
        // Delete old upload records and collect their blob keys
        oldBlobKeysToDelete = await repo.deleteUploadsForPost(postId_7);

        // Add new uploads
        if (uploads_0.length > 0) {
          for (const uploadData_0 of uploads_0) {
            const storeResult_0 = await blobStorage_3.storeFile({
              uri: uploadData_0.uri
            });
            const newUploadRecords = await repo.createUploadRecords(postId_7, [{
              key: storeResult_0.key,
              type: storeResult_0.mimeType
            }]);

            // Hash blob content
            const blobData_1 = await blobStorage_3.getBlobData(storeResult_0.key);
            if (blobData_1 && newUploadRecords[0]) {
              await collector_3.addBlob({
                id: storeResult_0.key,
                name: storeResult_0.key,
                content: blobData_1,
                createdAt: newUploadRecords[0].createdAt
              });
            }
          }
        }
      }

      // Delete old blobs after DB transaction succeeds
      if (oldBlobKeysToDelete.length > 0) {
        const failedBlobDeletions_0: string[] = [];
        await Promise.all(oldBlobKeysToDelete.map(async key_1 => {
          try {
            await blobStorage_3.delete(key_1);
          } catch (error_16) {
            console.warn('Failed to delete old blob after edit:', key_1, error_16);
            failedBlobDeletions_0.push(key_1);
          }
        }));
        if (failedBlobDeletions_0.length > 0) {
          console.warn(`[editPrivatePost] ${failedBlobDeletions_0.length} old blob(s) orphaned but DB records updated`);
        }
      }

      // Create snapshot and hash AFTER edit
      await collector_3.snapshotAndHash(postId_7);
      syncHashesAsync(collector_3.getHashes());
      return true;
    } catch (error_15) {
      await handleError(error_15, 'editing private post', true);
      return false;
    }
  };
  const getPrivatePostEditHistory = (postId_8: string) => readQuery(null, 'fetching private post edit history', async r_8 => {
    const postRow_2 = await r_8.fetchPrivatePost(postId_8);
    if (!postRow_2) return null;

    // Get snapshots in reverse chronological order (newest first)
    const snapshots = await r_8.getSnapshotsForPost(postId_8);

    // Filter to only show snapshots where content changed
    const edits: Array<{
      id: number;
      content: string;
      date: Date;
      avatarKey: string | null;
      robohash: string | null;
    }> = [];
    let previousContent = postRow_2.content;
    for (const snapshot_0 of snapshots) {
      if (snapshot_0.content !== previousContent) {
        edits.push({
          id: edits.length,
          content: snapshot_0.content,
          date: new Date(snapshot_0.createdAt),
          avatarKey: null,
          robohash: null
        });
      }
      previousContent = snapshot_0.content;
    }
    return {
      avatar: null as null,
      edits
    };
  });
  const clearEntireDatabase = async () => {
    // Only primary tab can write to avoid multi-tab conflicts
    if (!repo || !canWrite) throw new Error('Database not initialized, disabled, or not primary tab');
    try {
      await repo.clearEntireDatabase();
    } catch (error_17) {
      await handleError(error_17, 'clearing database', true);
      throw error_17;
    }
  };

  // Get raw private post data for hash verification
  const getRawPrivatePost = (postId_9: string) => readQuery(null, 'getting raw private post', async r_9 => {
    const postRow_3 = await r_9.fetchPrivatePost(postId_9);
    if (!postRow_3) return null;
    return {
      id: postRow_3.id,
      content: postRow_3.content,
      spaceId: postRow_3.spaceId ?? null,
      parentPostId: postRow_3.parentPostId ?? null,
      createdAt: new Date(postRow_3.createdAt)
    };
  });

  // Get all verification data for a post (for server verification)
  const getVerificationData = (postId_10: string) => readQuery(null, 'getting verification data', async r_10 => {
    const snapshotRecords = await r_10.getSnapshotsForPost(postId_10);
    if (snapshotRecords.length === 0) return null;

    // Sort ascending for verification
    const sorted = [...snapshotRecords].sort((a, b) => a.createdAt - b.createdAt);
    const snapshots_0: PostSnapshot[] = [];
    for (const record of sorted) {
      const snapshot_1 = await r_10.collectStoredSnapshot(record.id);
      if (snapshot_1) {
        snapshots_0.push(snapshot_1);
      }
    }
    return {
      snapshots: snapshots_0
    };
  });

  // Get database statistics for debugging
  const getDebugStats = async () => {
    if (!repo || disabled) {
      return {
        error: 'Database not available',
        disabled,
        dbExists: !!db
      };
    }
    try {
      const stats = await repo.getDebugStats();
      return {
        ...stats,
        canWrite
      };
    } catch (error_18) {
      return {
        error: String(error_18)
      };
    }
  };

  // Get paginated data from any table for debugging
  const getPaginatedTableData = async (tableName_0: string, page_2: number, limit_0 = 20) => {
    if (!repo || disabled) return {
      records: [],
      total: 0,
      error: 'Database not available'
    };
    try {
      return await repo.getPaginatedTableData(tableName_0, page_2, limit_0);
    } catch (error_19) {
      return {
        records: [],
        total: 0,
        error: String(error_19)
      };
    }
  };

  // List all available tables
  const getAvailableTables = () => {
    return ['post_engagements', 'factiii_engagements', 'mood_entries', 'private_posts', 'private_uploads', 'private_post_factiii_counts', 'private_post_factiiis', 'private_post_factiii_uploads', 'space_times'];
  };

  // Delete a single record by ID from a table
  const deleteRecord = async (tableName_1: string, recordId: string): Promise<{
    success: boolean;
    deletedBlob?: boolean;
    error?: string;
  }> => {
    if (!repo || !canWrite) {
      return {
        success: false,
        error: 'Database not available or not primary tab'
      };
    }
    try {
      const blobKey = await repo.deleteRecord(tableName_1, recordId);
      let deletedBlob = false;
      if (blobKey) {
        try {
          const blobStorage_4 = await getBlobStorage(userId!);
          await blobStorage_4.delete(blobKey);
          deletedBlob = true;
        } catch (blobError) {
          console.warn('Failed to delete associated blob:', blobError);
        }
      }
      return {
        success: true,
        deletedBlob
      };
    } catch (error_20) {
      return {
        success: false,
        error: String(error_20)
      };
    }
  };

  // Check if a table has blob associations
  const tableHasBlobAssociation = (tableName_2: string): boolean => {
    return tableName_2 === 'private_uploads';
  };

  // Delete records where ALL key fields are null (completely broken records)
  const deleteEmptyRecords = async () => {
    if (!repo || !canWrite) {
      return {
        deleted: 0,
        error: 'Database not available or not primary tab'
      };
    }
    try {
      const deleted = await repo.deleteEmptyRecords();
      return {
        deleted
      };
    } catch (error_21) {
      return {
        deleted: 0,
        error: String(error_21)
      };
    }
  };

  // Blob consistency utilities
  const checkBlobConsistency = async (): Promise<{
    orphanedBlobs: string[];
    danglingReferences: string[];
    totalBlobsInStorage: number;
    totalReferencesInDb: number;
  }> => {
    if (!repo || disabled) {
      return {
        orphanedBlobs: [],
        danglingReferences: [],
        totalBlobsInStorage: 0,
        totalReferencesInDb: 0
      };
    }
    try {
      const blobStorage_5 = await getBlobStorage(userId!);
      const blobKeys = await blobStorage_5.getAllKeys();
      const blobKeySet = new Set(blobKeys);
      const dbBlobKeys = await repo.getAllUploadKeys();
      const dbBlobKeySet = new Set(dbBlobKeys);
      const orphanedBlobs = blobKeys.filter(key_2 => !dbBlobKeySet.has(key_2));
      const danglingReferences = dbBlobKeys.filter(key_3 => !blobKeySet.has(key_3));
      return {
        orphanedBlobs,
        danglingReferences,
        totalBlobsInStorage: blobKeys.length,
        totalReferencesInDb: dbBlobKeys.length
      };
    } catch (error_22) {
      console.error('[BlobConsistency] Failed to check consistency:', error_22);
      return {
        orphanedBlobs: [],
        danglingReferences: [],
        totalBlobsInStorage: 0,
        totalReferencesInDb: 0
      };
    }
  };
  const cleanupOrphanedBlobs = async (): Promise<{
    deleted: number;
    failed: string[];
    error?: string;
  }> => {
    if (!repo || disabled) {
      return {
        deleted: 0,
        failed: [],
        error: 'Database not available'
      };
    }
    try {
      const blobStorage_6 = await getBlobStorage(userId!);
      const blobKeys_0 = await blobStorage_6.getAllKeys();
      const dbBlobKeys_0 = await repo.getAllUploadKeys();
      const dbBlobKeySet_0 = new Set(dbBlobKeys_0);
      const orphanedBlobs_0 = blobKeys_0.filter(key_4 => !dbBlobKeySet_0.has(key_4));
      if (orphanedBlobs_0.length === 0) return {
        deleted: 0,
        failed: []
      };
      const result_0 = await blobStorage_6.deleteMany(orphanedBlobs_0);
      return result_0;
    } catch (error_23) {
      console.error('[BlobConsistency] Failed to cleanup orphaned blobs:', error_23);
      return {
        deleted: 0,
        failed: [],
        error: String(error_23)
      };
    }
  };
  const cleanupDanglingReferences = async (): Promise<{
    deleted: number;
    error?: string;
  }> => {
    if (!repo || !canWrite) {
      return {
        deleted: 0,
        error: 'Database not available or not primary tab'
      };
    }
    try {
      const blobStorage_7 = await getBlobStorage(userId!);
      const blobKeys_1 = await blobStorage_7.getAllKeys();
      const blobKeySet_0 = new Set(blobKeys_1);
      const dbBlobKeys_1 = await repo.getAllUploadKeys();
      const danglingKeys = dbBlobKeys_1.filter(key_5 => !blobKeySet_0.has(key_5));
      if (danglingKeys.length === 0) return {
        deleted: 0
      };
      const deleted_0 = await repo.deleteUploadsByKeys(danglingKeys);
      return {
        deleted: deleted_0
      };
    } catch (error_24) {
      console.error('[BlobConsistency] Failed to cleanup dangling references:', error_24);
      return {
        deleted: 0,
        error: String(error_24)
      };
    }
  };

  // Inject test data for debugging purposes (Admin only)
  const injectTestData = async (count: number = 500, seedData?: {
    factiiiIds: number[];
    spaceIds: number[];
    postIds: string[];
  }) => {
    if (!db || !canWrite) {
      return {
        success: false,
        recordsCreated: {},
        error: 'Database not available or write not allowed'
      };
    }
    if (!userId) {
      return {
        success: false,
        recordsCreated: {},
        error: 'No user logged in'
      };
    }
    const {
      injectTestData: injectTestDataUtil
    } = await loadSeedData();
    return injectTestDataUtil(db, userId, SOURCE, count, seedData);
  };
  const registerTabVisibility = () => {
    if (typeof document === 'undefined') return () => {};
    const handler = () => {
      dispatch({
        type: databaseActions.SET_IS_TAB_ACTIVE,
        payload: document.visibilityState === 'visible'
      });
    };
    document.addEventListener('visibilitychange', handler);
    handler();
    return () => document.removeEventListener('visibilitychange', handler);
  };
  return {
    // Engagement tracking
    addOrUpdatePostEngagement,
    addOrUpdateFactiiiEngagement,
    getPostEngagement,
    getAllPostEngagements,
    getPaginatedPostEngagements,
    getAllFactiiiEngagements,
    getFactiiiEngagementsByDateRange,
    // Mood tracking
    addMoodEntry,
    getMoodEntriesByDateRange,
    getLatestMoodEntry,
    seedAdvancedData,
    // Health data
    getHealthPostsByDateRange,
    // Private posts
    createPrivatePost,
    fetchPrivatePosts,
    fetchPrivatePost,
    fetchPrivatePostReplies,
    editPrivatePost,
    deletePrivatePost,
    getPrivatePostEditHistory,
    uploadFromBase64,
    processPrivateFactiiis,
    fetchPrivateMyFactiiiTags,
    getPrivatePostWithBlobURLs,
    getRawPrivatePost,
    getVerificationData,
    // Database management
    clearEntireDatabase,
    // Debug utilities
    getDebugStats,
    getPaginatedTableData,
    getAvailableTables,
    deleteRecord,
    tableHasBlobAssociation,
    deleteEmptyRecords,
    // Blob consistency utilities
    checkBlobConsistency,
    cleanupOrphanedBlobs,
    cleanupDanglingReferences,
    // Test data
    injectTestData,
    registerTabVisibility,
    // State
    isReady: db !== null && !disabled,
    disabled,
    canWrite,
    acknowledgeError,
    // Hash sync controls
    hashingDisabled,
    toggleHashing,
    syncUnsentHashes
  };
};
export default useDatabase;